<template>
    <div class="veckooversikt">
        <h1>Veckoöversikt</h1>
        <KFilter
            name="veckooversikt"
            :schema="schema"
            :defaultValue="filterData"
            @submit="onFilterSubmit"
            :expanded="true"
        />

        <Button class="print" size="secondary" @click="print()" :disabled="anmalningar.length === 0">Skriv ut</Button>

        <div class="count">Visar {{ anmalningar.length }} operationer</div>

        <hr />

        <div ref="printablecontent">
            <div class="table-caption">Veckoöversikt operation</div>
            <div class="table-subcaption">
                <span>
                    Datum:
                    <strong>
                        Vecka {{ selectedDate.isoWeek() }}, {{ firstDayOfWeek.format("YYYY-MM-DD") }} -
                        {{ lastDayOfWeek.format("YYYY-MM-DD") }}
                    </strong>
                </span>
                <span>
                    Enhet: <strong>{{ getEnhet }}</strong>
                </span>
            </div>

            <!--
              CSS Grid replaces <table>. gridTemplateColumns is set dynamically
              so it always matches the number of visible columns.
              VeckooversiktList renders with display:contents, making its .vg-cell
              children direct grid items — column alignment without any HTML table.
            -->
            <div
                class="veckooversikt-grid"
                :style="{ gridTemplateColumns: `repeat(${visibleColumnCount}, minmax(min-content, 1fr))` }"
            >
                <div class="vg-header">
                    <div v-if="isVisible('dag')" class="vg-header-cell">Dag</div>
                    <div v-if="isVisible('optid')" class="vg-header-cell">Optid</div>
                    <div v-if="isVisible('sal')" class="vg-header-cell">Sal</div>
                    <div v-if="isVisible('operator')" class="vg-header-cell">Operatör</div>
                    <div v-if="isVisible('typoperation')" class="vg-header-cell">Typoperation/Huvudopkod</div>
                    <div v-if="isVisible('huvudopkod')" class="vg-header-cell">Huvudopkod</div>
                    <div v-if="isVisible('nivaer')" class="vg-header-cell">Nivåer</div>
                    <div v-if="isVisible('sida')" class="vg-header-cell">Sida</div>
                    <div v-if="isVisible('implantat')" class="vg-header-cell">Implantat</div>
                    <div v-if="isVisible('resurser')" class="vg-header-cell">Resurser</div>
                    <div v-if="isVisible('reop')" class="vg-header-cell">Reop</div>
                    <div v-if="isVisible('operationsinfo')" class="vg-header-cell">Operationsinfo</div>
                    <div v-if="isVisible('personId')" class="vg-header-cell">PersonID</div>
                    <div v-if="isVisible('patientnamn')" class="vg-header-cell">Patientnamn</div>
                </div>

                <VeckooversiktList v-for="(group, index) in groupedAnmalningar" :key="index" :group="group" />

                <div class="vg-footer-total">
                    Summa optid vecka {{ selectedDate.format("W") }}, {{ firstDayOfWeek.format("YYYY-MM-DD") }} -
                    {{ lastDayOfWeek.format("YYYY-MM-DD") }}<br />
                    <strong>{{ sumOptid }} min</strong>
                </div>
            </div>
        </div>
        <ModalBasic ref="dialog" />
    </div>
</template>

<script>
    import BaseTab from "@/tabs/mixins/BaseTab";

    export default {
        // eslint-disable-next-line vue/multi-word-component-names
        name: "Veckoöversikt",
        tabName: "Veckoöversikt",
        refreshTab: true,
        extends: BaseTab,
    };
</script>

<script setup>
    import Button from "@/components/Button.vue";
    import { chain, sumBy } from "lodash";
    import qs from "qs";
    import VeckooversiktList from "@/tabs/Operation/Oversikt/VeckooversiktList.vue";
    import { printInIframe } from "@/utils/printWindow";
    import moment from "moment/moment";
    import ModalBasic, { BasicModalVariant } from "@/components/generic/ModalBasic.vue";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import { klinikenApi, getConfig, getErrorMessage } from "@/api";
    import { openDialog } from "@/utils";
    import emitter from "tiny-emitter/instance";
    import { ConfigModuleName, OperationConfigModuleName } from "@/store/modules/types/configModuleTypes";
    import { useStore } from "@/store";
    import { computed, onMounted, onUnmounted, ref } from "vue";

    const store = useStore();

    const props = defineProps({
        tabId: Number,
        parameters: Object,
    });

    const schema = generateSchema({
        addToAll: { ignoreEmptyData: true },
        schema: [
            {
                component: "KText",
                type: "week",
                name: "yearWeek",
                label: "Vecka",
                ignoreEmptyData: false,
                nonClearableTag: true,
                tagValueFormatter: (value) => {
                    if (!value) return value;
                    const [year, week] = value.split("-W");
                    return `${year}-V${week}`;
                },
            },
            {
                component: "KAutocomplete",
                name: "operationsenhet",
                label: "Enhet",
                url: "/core/orgunits/",
                optionMapper: ({ id, name }) => ({ value: id, label: name }),
                urlParams: { vardenhetstyp: "operationsenhet", is_active: true, only_permitted: true },
                autoPosition: true,
                reduce: (option) => option,
                multiple: true,
            },
            {
                component: "KAutocomplete",
                name: "current_sal",
                label: "Sal",
                url: "/operation/salar/",
                optionMapper: ({ id, namn }) => ({ value: id, label: namn }),
                autoPosition: true,
                multiple: true,
            },
            {
                component: "ProfileAutocomplete",
                name: "current_bokad_huvudoperator",
                label: "Operatör",
                allowSearch: true,
                urlParams: { roll__code: "kirurg" },
                autoPosition: true,
                multiple: true,
            },
            {
                component: "TypoperationAutocomplete",
                name: "current_typoperation",
                label: "Typoperation/Huvudopkod",
                autoPosition: true,
                retrieveOptionsOnMount: true,
                multiple: true,
            },
            {
                component: "KAutocomplete",
                name: "implantat",
                label: "Implantat",
                url: "/operation/implantat/",
                optionMapper: ({ id, namn }) => ({ value: id, label: namn }),
                autoPosition: true,
                multiple: true,
            },
            {
                component: "KAutocomplete",
                name: "resurser",
                label: "Resurser",
                url: "/operation/resurser/",
                optionMapper: ({ id, namn }) => ({ value: id, label: namn }),
                autoPosition: true,
                multiple: true,
            },
            {
                component: "JaNejAutocomplete",
                name: "reop",
                label: "Reop",
                autoPosition: true,
                toBoolean: true,
            },
            {
                component: "KText",
                name: "patient__personId",
                label: "PersonID",
            },
            {
                component: "KCheckbox",
                name: "sortBySal",
                label: "Sortera på sal",
                valueType: "boolean",
                ignoreEmptyData: false,
                tagShowValue: false,
                valign: "center",
            },
        ],
    });

    const anmalningar = ref([]);
    const groupedAnmalningar = ref([]);
    const filterData = ref({
        yearWeek: `${moment().year()}-W${moment().isoWeek()}`,
        operationsenhet: null,
        sortBySal: false,
    });
    const printablecontent = ref(null);
    const dialog = ref(null);

    const can = computed(() => store.getters["ability/can"]);

    const selectedDate = computed(() => {
        if (!filterData.value.yearWeek) return moment();
        const [year, week] = filterData.value.yearWeek.split("-W");
        return moment().year(year).isoWeek(week);
    });

    const firstDayOfWeek = computed(() => selectedDate.value.clone().isoWeekday(1));
    const lastDayOfWeek = computed(() => selectedDate.value.clone().isoWeekday(7));

    const sumOptid = computed(() => sumBy(anmalningar.value, (anmalan) => anmalan.optid));

    const getEnhet = computed(() => {
        const enhet = filterData.value.operationsenhet;
        if (!enhet || (Array.isArray(enhet) && enhet.length === 0)) return "Alla enheter";
        if (Array.isArray(enhet)) return enhet.map((e) => e.label).join(", ");
        return enhet.label ?? "Alla enheter";
    });

    const veckooversiktConfig = computed(() =>
        store.getters["config/configByName"]({
            moduleName: ConfigModuleName.Operation,
            subModuleName: OperationConfigModuleName.OperationVeckooversikt,
        })
    );

    const COLUMN_NAMES = [
        "dag",
        "optid",
        "sal",
        "operator",
        "typoperation",
        "huvudopkod",
        "nivaer",
        "sida",
        "implantat",
        "resurser",
        "reop",
        "operationsinfo",
        "personId",
        "patientnamn",
    ];

    const visibleColumnCount = computed(() => COLUMN_NAMES.filter(isVisible).length);

    function isVisible(fieldName) {
        const fields = veckooversiktConfig.value?.fields;
        if (!fields || !(fieldName in fields)) return true;
        return store.getters["config/shouldBeVisibleByName"]({
            moduleName: ConfigModuleName.Operation,
            subModuleName: OperationConfigModuleName.OperationVeckooversikt,
            name: fieldName,
        });
    }

    async function onFilterSubmit({ formData }) {
        filterData.value = {
            ...formData,
            yearWeek: formData.yearWeek || filterData.value.yearWeek,
        };
        await filter();
    }

    async function filter() {
        store.dispatch("tabs/displaySpinner", { id: props.tabId, display: true });
        try {
            const params = {
                bokad_operation_datum__gte: firstDayOfWeek.value.format("YYYY-MM-DD"),
                bokad_operation_datum__lte: lastDayOfWeek.value.format("YYYY-MM-DD"),
                operationsenhet: Array.isArray(filterData.value.operationsenhet)
                    ? filterData.value.operationsenhet.map((e) => e.value)
                    : (filterData.value.operationsenhet?.value ?? filterData.value.operationsenhet),
                current_sal: filterData.value.current_sal,
                current_bokad_huvudoperator: filterData.value.current_bokad_huvudoperator,
                current_typoperation: filterData.value.current_typoperation,
                implantat: filterData.value.implantat,
                resurser: filterData.value.resurser,
                reop: filterData.value.reop,
                patient__personId: filterData.value.patient__personId || undefined,
                ordering: filterData.value.sortBySal
                    ? "bokad_operation_datum,current_sal__ordning,current_operation_tid"
                    : "bokad_operation_datum,current_operation_tid",
            };
            const response = await klinikenApi.get(
                `/operation/oversikt/?${qs.stringify(params, { indices: false, skipNulls: true })}`,
                getConfig({}, "Veckoöversikt")
            );
            anmalningar.value = response?.data?.results ?? response.data;
            groupedAnmalningar.value = chain(anmalningar.value)
                .groupBy((anmalan) => anmalan.bokad_operation_datum)
                .map((anmalningar, datum) => ({ anmalningar, datum }))
                .value();
        } catch (error) {
            openDialog(getErrorMessage(error), "error");
        } finally {
            store.dispatch("tabs/displaySpinner", { id: props.tabId, display: false });
        }
    }

    async function print() {
        if (!can.value({ action: "read", subject: "veckooversikt" })) return;
        if (!printablecontent.value) return;
        try {
            await printInIframe({
                url: "/operation/anmalan/utskrift/",
                items: anmalningar.value,
                logName: "Veckoöversikt",
                contentElement: printablecontent.value,
                orientation: "landscape",
                title: `Veckoöversikt ${moment().format("YYYY-MM-DD HH:mm")}`,
            });
        } catch (_) {
            dialog.value?.show({
                variant: BasicModalVariant.ALERT,
                title: "Det gick inte att få ett utskrift-ID.",
                okButton: "OK",
            });
        }
    }

    onMounted(async () => {
        await filter();
        emitter.on(`refreshTab-${props.tabId}`, filter);
    });

    onUnmounted(() => {
        emitter.off(`refreshTab-${props.tabId}`, filter);
    });
</script>

<style lang="scss" scoped>
    @import "@/style/variables";

    /*
     * Screen-only component chrome (button, counter, divider).
     * These are never inside the printable content div so nesting
     * under .veckooversikt is fine — they only need to work on screen.
     */
    .veckooversikt {
        .button.print {
            margin-top: 20px;
        }

        .count {
            font-style: normal;
            font-weight: 400;
            font-size: 22px;
            line-height: 24px;
            margin: 25px 0 15px;
        }

        hr {
            border: 1px solid $list-border-color;
        }
    }

    /*
     * ─────────────────────────────────────────────────────────────────
     *  PRINTABLE CONTENT STYLES
     *  These are intentionally NOT nested inside .veckooversikt so the
     *  compiled scoped selectors do not require that ancestor.
     *
     *  The Vue scoped style mechanism still adds [data-v-XXXX] to every
     *  selector (preventing leakage to other components), but the
     *  selectors will also match inside the print iframe where the
     *  .veckooversikt wrapper is absent.
     * ─────────────────────────────────────────────────────────────────
     */

    /* Caption / subcaption */
    .table-caption {
        font-style: normal;
        font-weight: 400;
        font-size: $font-size-small;
        line-height: 28px;
        color: $color-active-dark;
        font-family:
            Roboto Medium,
            sans-serif;
        white-space: nowrap;
    }

    .table-subcaption {
        display: flex;
        align-items: flex-end;
        gap: 14px;

        font-style: normal;
        font-weight: 400;
        font-size: $font-size-small;
        line-height: 28px;
        color: $color-active-dark;

        strong {
            color: $color-active-dark;
            font-size: $font-size-small;
        }
    }

    /* Grid container */
    .veckooversikt-grid {
        display: grid;
        width: 100%;

        font-style: normal;
        font-weight: 400;
        font-size: $font-size-small;
        line-height: 18px;

        color: $dark-text-color;

        /* ── Header row ──────────────────────────────────────────── */
        .vg-header {
            display: contents;
        }

        .vg-header-cell {
            padding: 4px 10px;
            background: $color-bg-table-header;
            font-weight: 500;
            color: $color-table-header;
            border-bottom: 2px solid $color-table-header-line;
            align-self: end; /* vertical-align: bottom equivalent */
        }

        /* ── Data cells (from VeckooversiktList via ::v-deep) ────── */
        ::v-deep .vg-cell {
            padding: 4px 10px;
            align-self: start; /* vertical-align: top */
        }

        /* ── Footer mixin (per-day + grand total) ────────────────── */
        @mixin footer-style {
            text-align: right;
            background: linear-gradient(270deg, #f1f4f8 0%, rgba(241, 244, 248, 0) 52.01%);
            border-top: 1px solid $list-border-color;
            border-bottom: 2px solid $color-table-header-line;
            font-weight: 500;
            color: $color-table-header;
            padding: 4px 10px;

            strong {
                font-size: $font-size-small;
                font-weight: 500;
                line-height: 18px;
                color: $dark-text-color;
            }
        }

        ::v-deep .vg-footer-day {
            @include footer-style;
        }

        /* ── Grand total footer ──────────────────────────────────── */
        .vg-footer-total {
            grid-column: 1 / -1;
            @include footer-style;
            padding: 10px;
        }
    }
</style>
